import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Edit, Trash2, ExternalLink, Percent, Building2, Globe,
  RefreshCw, CheckCircle2, XCircle, Clock, PlayCircle, X, Loader2,
} from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog'
import api from '@/utils/api'
import { cn } from '@/lib/utils'

const CATEGORIES = ['Kabel','Leitungen','Schalter','Steckdosen','Verteiler','Beleuchtung','Werkzeug','Befestigung','Rohre','Sonstiges']

interface Supplier {
  id: number; name: string; website?: string; loginUrl?: string
  searchUrlPattern?: string; isActive: boolean
}

interface Rebate {
  id: number; rebatePercent: number; category?: string
  excludedCategories: string[]; validFrom?: string; validUntil?: string; notes?: string
}

// --- Sub-Components ---
function SupplierForm({ supplier, onClose, onSuccess }: { supplier: Supplier | null; onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({ name: supplier?.name || '', website: supplier?.website || '', loginUrl: supplier?.loginUrl || '', searchUrlPattern: supplier?.searchUrlPattern || '', isActive: supplier?.isActive !== false })
  const [saving, setSaving] = useState(false)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) { toast.error('Name ist erforderlich'); return }
    setSaving(true)
    try {
      if (supplier?.id) await api.put(`/suppliers/${supplier.id}`, formData)
      else await api.post('/suppliers', formData)
      onSuccess()
    } catch (err: any) { toast.error(err.response?.data?.error || 'Fehler beim Speichern') }
    finally { setSaving(false) }
  }
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {[
        { label: 'Name *', key: 'name', ph: 'z.B. Sonepar', req: true },
        { label: 'Website', key: 'website', ph: 'https://www.beispiel.de' },
        { label: 'Login-URL', key: 'loginUrl', ph: 'https://www.beispiel.de/login' },
        { label: 'Such-URL-Muster', key: 'searchUrlPattern', ph: 'https://www.beispiel.de/search?q={query}' },
      ].map(f => (
        <div key={f.key} className="space-y-2">
          <label className="text-sm font-medium">{f.label}</label>
          <Input value={(formData as any)[f.key]} onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })} placeholder={f.ph} required={f.req} />
        </div>
      ))}
      <div className="flex items-center gap-2">
        <input type="checkbox" id="isActive" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} className="h-4 w-4 rounded" />
        <label htmlFor="isActive" className="text-sm">Aktiv</label>
      </div>
      <div className="flex justify-end gap-3 border-t pt-4">
        <Button variant="outline" type="button" onClick={onClose}>Abbrechen</Button>
        <Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Speichern</Button>
      </div>
    </form>
  )
}

function RebateForm({ rebate, onSave, onCancel }: { rebate: Rebate | null; onSave: (data: any) => Promise<void>; onCancel: () => void }) {
  const [formData, setFormData] = useState({ rebatePercent: rebate?.rebatePercent || '', category: rebate?.category || '', excludedCategories: rebate?.excludedCategories || [] as string[], validFrom: rebate?.validFrom || '', validUntil: rebate?.validUntil || '', notes: rebate?.notes || '' })
  const [saving, setSaving] = useState(false)
  const toggleExcluded = (cat: string) => {
    setFormData(prev => ({ ...prev, excludedCategories: prev.excludedCategories.includes(cat) ? prev.excludedCategories.filter(c => c !== cat) : [...prev.excludedCategories, cat] }))
  }
  const handleSubmit = async (e: React.FormEvent) => { e.preventDefault(); setSaving(true); await onSave(formData); setSaving(false) }
  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border bg-muted/50 p-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><label className="text-sm font-medium">Rückvergütung (%)</label><Input type="number" step="0.1" min="0" max="100" value={formData.rebatePercent} onChange={(e) => setFormData({ ...formData, rebatePercent: e.target.value })} required /></div>
        <div className="space-y-2"><label className="text-sm font-medium">Nur für Kategorie</label><Input value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} placeholder="Leer = alle" /></div>
      </div>
      {!formData.category && (
        <div>
          <label className="mb-2 block text-sm font-medium">Ausgeschlossene Kategorien</label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <button key={cat} type="button" onClick={() => toggleExcluded(cat)} className={cn('inline-flex items-center gap-1 rounded-full border-2 px-3 py-1.5 text-sm font-medium transition-colors', formData.excludedCategories.includes(cat) ? 'border-red-300 bg-red-100 text-red-700' : 'border-transparent bg-muted text-muted-foreground hover:bg-muted/80')}>
                {formData.excludedCategories.includes(cat) && <X className="h-3 w-3" />}{cat}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><label className="text-sm font-medium">Gültig von</label><Input type="date" value={formData.validFrom} onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })} /></div>
        <div className="space-y-2"><label className="text-sm font-medium">Gültig bis</label><Input type="date" value={formData.validUntil} onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })} /></div>
      </div>
      <div className="space-y-2"><label className="text-sm font-medium">Notizen</label><Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} /></div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" type="button" onClick={onCancel}>Abbrechen</Button>
        <Button size="sm" type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Speichern</Button>
      </div>
    </form>
  )
}

function RebatesManager({ supplierId }: { supplierId: number }) {
  const queryClient = useQueryClient()
  const [editRebate, setEditRebate] = useState<Rebate | null>(null)
  const [showForm, setShowForm] = useState(false)

  const { data: rebates = [], isLoading } = useQuery<Rebate[]>({
    queryKey: ['rebates', supplierId],
    queryFn: async () => {
      const res = await api.get(`/suppliers/${supplierId}/rebates`)
      return res.data.map((r: any) => ({ ...r, excludedCategories: r.excludedCategories ? JSON.parse(r.excludedCategories) : [] }))
    },
  })

  const saveMutation = useMutation({
    mutationFn: (data: any) =>
      editRebate?.id
        ? api.put(`/suppliers/${supplierId}/rebates/${editRebate.id}`, data)
        : api.post(`/suppliers/${supplierId}/rebates`, data),
    onSuccess: () => {
      toast.success(editRebate?.id ? 'Aktualisiert' : 'Erstellt')
      setShowForm(false); setEditRebate(null)
      queryClient.invalidateQueries({ queryKey: ['rebates', supplierId] })
    },
    onError: (err: any) => { toast.error(err.response?.data?.error || 'Fehler') },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/suppliers/${supplierId}/rebates/${id}`),
    onSuccess: () => { toast.success('Gelöscht'); queryClient.invalidateQueries({ queryKey: ['rebates', supplierId] }) },
    onError: () => { toast.error('Fehler') },
  })

  const handleSave = async (data: any) => { saveMutation.mutate(data) }
  const handleDelete = (id: number) => {
    if (!confirm('Wirklich löschen?')) return
    deleteMutation.mutate(id)
  }

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Rückvergütungen reduzieren den effektiven Preis</p>
        <Button size="sm" onClick={() => { setEditRebate(null); setShowForm(true) }}><Plus className="h-4 w-4" /> Neu</Button>
      </div>
      {showForm && <RebateForm rebate={editRebate} onSave={handleSave} onCancel={() => { setShowForm(false); setEditRebate(null) }} />}
      {rebates.length > 0 ? (
        <div className="overflow-hidden rounded-lg border">
          <table className="w-full">
            <thead><tr className="border-b bg-muted/50">
              {['Kategorie','Ausgeschlossen','Prozent','Gültig','Notizen',''].map(h => <th key={h} className="px-4 py-2 text-left text-xs font-medium uppercase text-muted-foreground">{h}</th>)}
            </tr></thead>
            <tbody>
              {rebates.map(r => (
                <tr key={r.id} className="border-b hover:bg-muted/50">
                  <td className="px-4 py-2 text-sm">{r.category || <span className="italic text-muted-foreground">Alle</span>}</td>
                  <td className="px-4 py-2">{r.excludedCategories?.length > 0 ? <div className="flex flex-wrap gap-1">{r.excludedCategories.map((c,i) => <Badge key={i} variant="destructive" className="text-xs">{c}</Badge>)}</div> : '-'}</td>
                  <td className="px-4 py-2 text-right font-medium text-green-600">-{r.rebatePercent}%</td>
                  <td className="px-4 py-2 text-center text-sm text-muted-foreground">{r.validFrom || r.validUntil ? `${r.validFrom||'∞'} - ${r.validUntil||'∞'}` : 'Unbegrenzt'}</td>
                  <td className="max-w-[200px] truncate px-4 py-2 text-sm text-muted-foreground">{r.notes || '-'}</td>
                  <td className="px-4 py-2"><div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => { setEditRebate(r); setShowForm(true) }}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(r.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="py-8 text-center text-muted-foreground"><Percent className="mx-auto mb-3 h-12 w-12 opacity-30" /><p>Keine Rückvergütungen</p></div>
      )}
    </div>
  )
}

// --- Main Component ---
export default function Lieferanten() {
  const queryClient = useQueryClient()
  const [editModal, setEditModal] = useState<{ open: boolean; supplier: Supplier | null }>({ open: false, supplier: null })
  const [rebateModal, setRebateModal] = useState<{ open: boolean; supplier: Supplier | null }>({ open: false, supplier: null })
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; supplier: Supplier | null }>({ open: false, supplier: null })
  const [testingSupplier, setTestingSupplier] = useState<number | null>(null)

  const { data: suppliers = [], isLoading } = useQuery<Supplier[]>({
    queryKey: ['suppliers'],
    queryFn: () => api.get('/suppliers').then((r) => r.data),
  })

  const { data: scraperStatus = null } = useQuery<{ isRunning: boolean } | null>({
    queryKey: ['scraper-status'],
    queryFn: () => api.get('/scraper/status').then((r) => r.data).catch(() => null),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/suppliers/${id}`),
    onSuccess: () => {
      toast.success('Gelöscht')
      setDeleteModal({ open: false, supplier: null })
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
    },
    onError: (err: any) => { toast.error(err.response?.data?.error || 'Fehler') },
  })

  const runScraperMutation = useMutation({
    mutationFn: () => api.post('/scraper/run'),
    onSuccess: () => { toast.success('Scraper gestartet'); queryClient.invalidateQueries({ queryKey: ['scraper-status'] }) },
    onError: () => { toast.error('Fehler beim Starten') },
  })

  const handleDelete = () => {
    if (!deleteModal.supplier) return
    deleteMutation.mutate(deleteModal.supplier.id)
  }
  const handleTestScraper = async (s: Supplier) => {
    setTestingSupplier(s.id)
    try { const r = await api.post(`/scraper/test/${s.name.toLowerCase()}`); r.data.success && r.data.loginSuccess ? toast.success(`${s.name}: Login erfolgreich!`) : toast.error(`${s.name}: Login fehlgeschlagen`) }
    catch (err: any) { toast.error(`Test fehlgeschlagen: ${err.message}`) }
    finally { setTestingSupplier(null) }
  }
  const handleRunScraper = () => { runScraperMutation.mutate() }

  return (
    <div>
      <PageHeader title="Lieferanten" description={`${suppliers.length} Lieferanten konfiguriert`} actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRunScraper} disabled={scraperStatus?.isRunning}>
            <RefreshCw className={cn('h-4 w-4', scraperStatus?.isRunning && 'animate-spin')} /> {scraperStatus?.isRunning ? 'Laeuft...' : 'Preise aktualisieren'}
          </Button>
          <Button onClick={() => setEditModal({ open: true, supplier: null })}><Plus className="h-4 w-4" /> Neuer Lieferant</Button>
        </div>
      } />

      {scraperStatus && (
        <Card className="mb-6"><CardContent className="flex items-center justify-between py-3">
          <div className="flex items-center gap-3"><div className={cn('h-3 w-3 rounded-full', scraperStatus.isRunning ? 'animate-pulse bg-yellow-400' : 'bg-green-400')} /><span className="text-sm text-muted-foreground">Scraper: {scraperStatus.isRunning ? 'Laeuft' : 'Bereit'}</span></div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Clock className="h-4 w-4" />Nächster Lauf: 03:00</div>
        </CardContent></Card>
      )}

      {isLoading ? <div className="space-y-2">{Array.from({length:3}).map((_,i) => <Skeleton key={i} className="h-16 w-full" />)}</div> : (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="w-full">
            <thead><tr className="border-b bg-muted/50">
              {['Lieferant','Website','Status','Scraper','Aktionen'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>)}
            </tr></thead>
            <tbody>
              {suppliers.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Keine Lieferanten</td></tr> :
              suppliers.map(s => (
                <tr key={s.id} className="border-b last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3"><div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded bg-muted"><Building2 className="h-5 w-5 text-muted-foreground" /></div><div><span className="text-sm font-medium">{s.name}</span>{s.loginUrl && <p className="max-w-xs truncate text-xs text-muted-foreground">{s.loginUrl}</p>}</div></div></td>
                  <td className="px-4 py-3 text-sm">{s.website ? <a href={s.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary-500 hover:text-primary-700"><Globe className="h-4 w-4" /><span className="max-w-[200px] truncate">{s.website}</span></a> : '-'}</td>
                  <td className="px-4 py-3">{s.isActive ? <Badge variant="success"><CheckCircle2 className="mr-1 h-3 w-3" />Aktiv</Badge> : <Badge variant="secondary"><XCircle className="mr-1 h-3 w-3" />Inaktiv</Badge>}</td>
                  <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => handleTestScraper(s)} disabled={testingSupplier === s.id}>{testingSupplier === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Testen</Button></td>
                  <td className="px-4 py-3"><div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setRebateModal({ open: true, supplier: s })} title="Rückvergütungen"><Percent className="h-4 w-4 text-green-600" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setEditModal({ open: true, supplier: s })}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeleteModal({ open: true, supplier: s })}><Trash2 className="h-4 w-4" /></Button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editModal.open} onOpenChange={(o) => !o && setEditModal({ open: false, supplier: null })}>
        <DialogContent><DialogHeader><DialogTitle>{editModal.supplier ? 'Lieferant bearbeiten' : 'Neuer Lieferant'}</DialogTitle></DialogHeader>
          <SupplierForm supplier={editModal.supplier} onClose={() => setEditModal({ open: false, supplier: null })} onSuccess={() => { setEditModal({ open: false, supplier: null }); queryClient.invalidateQueries({ queryKey: ['suppliers'] }); toast.success(editModal.supplier ? 'Aktualisiert' : 'Erstellt') }} />
        </DialogContent>
      </Dialog>

      {/* Rebates Dialog */}
      <Dialog open={rebateModal.open} onOpenChange={(o) => !o && setRebateModal({ open: false, supplier: null })}>
        <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>Rückvergütungen — {rebateModal.supplier?.name}</DialogTitle></DialogHeader>
          {rebateModal.supplier && <RebatesManager supplierId={rebateModal.supplier.id} />}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteModal.open} onOpenChange={(o) => !o && setDeleteModal({ open: false, supplier: null })}>
        <DialogContent><DialogHeader><DialogTitle>Lieferant löschen</DialogTitle><DialogDescription>Moechten Sie "{deleteModal.supplier?.name}" wirklich löschen? Alle Rückvergütungen werden ebenfalls gelöscht.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteModal({ open: false, supplier: null })}>Abbrechen</Button><Button variant="destructive" onClick={handleDelete}>Löschen</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
